import React, { useEffect, useState } from "react";
import { Box, Chip, Skeleton, Stack, Typography } from "@mui/material";
import sh from "bundles/common/utils/sh";
import { T, cardSx, SectionLabel, EmptyNote } from "bundles/Internal/components/platform_analytics/PlatformAnalyticsShared";

// Human labels for the FeatureEvent `feature` keys tracked across the recruiter SPA.
const FEATURE_LABELS = {
  recruiter_nav: "Navigation",
  dashboard: "Dashboard & stats",
  jobs: "Job offers",
  kanban: "Kanban board",
  cvtheque: "CVthèque",
  billing: "Billing / pricing",
  settings: "Settings",
};

const STATUS_STYLE = {
  active: { label: "Active (30d)", bg: "rgba(16,150,90,0.12)", color: "hsl(142 60% 30%)" },
  used: { label: "Used", bg: "hsl(140 6% 93%)", color: T.dim },
  never: { label: "Never", bg: "rgba(211,47,47,0.10)", color: "#D32F2F" },
};

const classify = (row) => {
  if (!row.ever) return "never";
  if (row.count_30d > 0) return "active";
  return "used";
};

const formatDate = (value) => {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Never";
  return new Intl.DateTimeFormat("fr-BE", { year: "2-digit", month: "2-digit", day: "2-digit" }).format(d);
};

const FeatureAdoptionPanel = ({ companyId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setLoading(true);
    sh.get(`/internal/companies/${companyId}/feature_adoption`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const features = data?.features || [];

  return (
    <Box sx={{ mt: 3 }}>
      <SectionLabel note="In-product feature adoption for this company's recruiters, captured via client-side telemetry (FeatureEvent). Covers browsing that leaves no other trace: which tools they open, not the outcomes (reveals, moves, applications are tracked elsewhere). 'Active' = used in the last 30 days.">
        Feature adoption
      </SectionLabel>

      <Box sx={{ ...cardSx, mt: 1, p: 0, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={40} sx={{ mb: 0.5, bgcolor: "hsl(140 8% 92%)" }} />
            ))}
          </Box>
        ) : features.length === 0 ? (
          <EmptyNote>No product activity recorded yet</EmptyNote>
        ) : (
          features.map((row, idx) => {
            const status = classify(row);
            const style = STATUS_STYLE[status];
            return (
              <Box
                key={row.feature}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderTop: idx === 0 ? "none" : `1px solid ${T.border}`,
                }}
              >
                <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg }}>
                  {FEATURE_LABELS[row.feature] || row.feature}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {row.ever && (
                    <Typography sx={{ fontSize: 11.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim }}>
                      {row.count}x · last {formatDate(row.last_used_at)}
                    </Typography>
                  )}
                  <Chip
                    label={style.label}
                    size="small"
                    sx={{
                      backgroundColor: style.bg,
                      color: style.color,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      fontSize: 10,
                      fontWeight: 600,
                      borderRadius: "6px",
                      height: 20,
                    }}
                  />
                </Stack>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default FeatureAdoptionPanel;
